import { msg } from "@lingui/core/macro";
import { createEffect, createMemo, createSignal, For, Show } from "solid-js";
import { useQuery } from "@tanstack/solid-query";
import { useSearch } from "@tanstack/solid-router";
import { Badge, Card, CardContent, CardHeader, CardTitle, Skeleton } from "@fleet/ui";
import { Button, buttonVariants } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useI18n } from "@/providers/i18n-provider";
import { api, fetchApi, SERVER_BASE_URL } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import { queryKeys, sharedApi } from "@gymlog/shared";
import { showToast } from "@/components/ui/toast";
import { Link2, ShieldCheck } from "@fleet/icons";
import { QueryErrorState } from "@/components/ui/query-error-state";

type ProviderId = "google" | "apple";

const PROVIDER_LABELS: Record<ProviderId, string> = {
  google: "Google",
  apple: "Apple",
};

/** Provider state changes rarely; cache it aggressively (5 min). */
const PROVIDERS_STALE_TIME_MS = 5 * 60 * 1000;

/** Builds the start URL for the LINKING flow for a provider. */
function linkUrl(provider: ProviderId): string {
  return `${SERVER_BASE_URL.replace(/\/$/u, "")}/api/auth/link-social/${provider}`;
}

/**
 * "Connected accounts": allows linking/unlinking Google and Apple from the
 * profile. Only shows ENABLED providers in the backend. Respects the last-method
 * safeguard (the backend rejects leaving the user without access).
 */
export function ConnectedAccountsSection() {
  const { t } = useI18n();
  // Reads ?linked=<provider> to show feedback after returning from the callback.
  const search = useSearch({ strict: false }) as () => { linked?: string };
  const [confirmUnlink, setConfirmUnlink] = createSignal<ProviderId | null>(null);
  const [busy, setBusy] = createSignal(false);

  // P23 / CC-60 · phase 2 — the fix belongs at the SOURCE, not in rendering.
  //
  // `sharedApi.*` is `openapi-fetch`: it NEVER throws, it returns `{data, error}`.
  // These two `queryFn`s did `return data ?? {…}`, so with the API down `data`
  // was `undefined`, the `??` FABRICATED a default object and the query resolved
  // in **SUCCESS**. Consequences measured with a browser (sustained 500 on
  // `login-methods`, `social/providers` at 200):
  //
  //   · the `<QueryErrorState testId="connected-accounts-error">` below was
  //     DEAD CODE — it never rendered;
  //   · the `isSuccess` guard that P23 put against I2 ALSO did not protect,
  //     because the state WAS `success`: the card claimed «Google · Not connected»
  //     + «Connect» + «This is your only sign-in method» for an account that
  //     may be perfectly linked. A lie about the account's real state, not a
  //     loading detail.
  //
  // `fetchApi` (→ `handleApiResponse`) THROWS on any non-2xx and on an empty body,
  // which is the only thing that makes TanStack switch to `error`.
  //
  // CAREFUL, deliberate exception: the SESSION `queryFn` (`querySession` in
  // `lib/auth.ts`) MUST keep swallowing the error — that is what sustains guest
  // mode. It is not converted.
  const providersQuery = useQuery(() => ({
    queryKey: queryKeys.auth.socialProviders(),
    queryFn: async () => fetchApi(sharedApi.socialProviders(api)),
    staleTime: PROVIDERS_STALE_TIME_MS,
    retry: false,
  }));

  const methodsQuery = useQuery(() => ({
    queryKey: queryKeys.auth.loginMethods(),
    queryFn: async () => fetchApi(sharedApi.loginMethods(api)),
    retry: false,
  }));

  /** Did either load fail? Governs whether the section is SHOWN. */
  const hasLoadError = createMemo(() => providersQuery.isError || methodsQuery.isError);

  const enabledProviders = createMemo<ProviderId[]>(() => {
    // P23/CC-60 · I2: `createMemo` is eager — without this guard this read
    // suspends mounting the section (and with it the /profile route).
    if (!providersQuery.isSuccess) {
      return [];
    }
    const p = providersQuery.data;
    if (!p) {
      return [];
    }
    const list: ProviderId[] = [];
    if (p.google) {
      list.push("google");
    }
    if (p.apple) {
      list.push("apple");
    }
    return list;
  });

  // P23/CC-60 · I2: the `isSuccess` guard prevents reading `.data` without a value
  // (`createMemo` is eager and that read suspended the entire /profile route).
  // What it did NOT do was prevent the lie: until phase 2 the state with the API
  // down WAS `success` (an object fabricated by the `??` in `queryFn`) and these
  // memos returned «no linked providers» as if it were data from the server.
  // That is fixed above, at the SOURCE; the guard remains necessary for I2.
  const linkedProviders = createMemo(() =>
    methodsQuery.isSuccess ? (methodsQuery.data?.providers ?? []) : [],
  );
  const hasPassword = createMemo(() =>
    methodsQuery.isSuccess ? (methodsQuery.data?.has_password ?? false) : false,
  );

  const isLinked = (provider: ProviderId) => linkedProviders().includes(provider);

  // Total number of sign-in methods (password + each linked social account).
  const totalMethods = createMemo(() => linkedProviders().length);

  // Is this the LAST method? (it cannot be unlinked without establishing another).
  const isLastMethod = (provider: ProviderId) =>
    isLinked(provider) && totalMethods() <= 1 && !hasPassword();

  const handleUnlink = async (provider: ProviderId) => {
    setBusy(true);
    try {
      const { error } = await sharedApi.unlinkAccount(api, provider);
      if (error) {
        const { message } = error;
        if (message.includes("only sign-in method")) {
          showToast(t(msg`Set a password before unlinking your only sign-in method.`), "error");
        } else {
          showToast(t(msg`Could not unlink the account. Please try again.`), "error");
        }
        return;
      }
      showToast(t(msg`Account unlinked.`), "success");
      await queryClient.invalidateQueries({
        queryKey: queryKeys.auth.loginMethods(),
      });
    } finally {
      setBusy(false);
      setConfirmUnlink(null);
    }
  };

  // Feedback after returning from the linking flow (?linked=<provider>).
  let linkedFeedbackDone = false;
  createEffect(() => {
    const provider = search().linked;
    if (provider && !linkedFeedbackDone) {
      linkedFeedbackDone = true;
      showToast(t(msg`Account connected.`), "success");
      void queryClient.invalidateQueries({
        queryKey: queryKeys.auth.loginMethods(),
      });
    }
  });

  // P23/CC-60 · phase 2 — COROLLARY of the fix: the section must ALSO be shown
  // when loading failed. With `enabledProviders().length > 0` alone, a
  // `social/providers` failure hid it entirely: the error branch became
  // unreachable by another route and the failure disappeared without a trace
  // (the same lie, silently).
  return (
    <Show when={enabledProviders().length > 0 || hasLoadError()}>
      <Card>
        <CardHeader class="flex flex-row items-center gap-2 space-y-0">
          <ShieldCheck class="h-5 w-5 text-primary" />
          <CardTitle>{t(msg`Connected accounts`)}</CardTitle>
        </CardHeader>
        <CardContent class="space-y-3">
          {/* P23/CC-60 - I3: "no response" != "nothing here". On error
              `isLoading` is FALSE, so the guard below let it pass, `data`
              was `undefined`, and the screen claimed there are no items. WRAP
              the block: chaining `&& !isError` would render the fallback of `<Show>`
              and leave error box AND skeleton simultaneously, forever.
              Phase 2: until `queryFn` stopped fabricating the default
              object, `isError` NEVER became `true` and all of this was
              dead code. */}
          <Show when={hasLoadError()}>
            <QueryErrorState
              testId="connected-accounts-error"
              compact
              onRetry={() => {
                if (providersQuery.isError) {
                  void providersQuery.refetch();
                }
                if (methodsQuery.isError) {
                  void methodsQuery.refetch();
                }
              }}
            />
          </Show>
          <Show when={!hasLoadError()}>
            <Show
              when={!methodsQuery.isPending && !providersQuery.isPending}
              fallback={
                <div class="space-y-3">
                  <Skeleton class="h-14 w-full rounded-xl" />
                  <Skeleton class="h-14 w-full rounded-xl" />
                </div>
              }
            >
              <For each={enabledProviders()}>
                {(provider) => (
                  <div class="flex items-center gap-4 rounded-xl border border-border bg-muted/30 p-4">
                    <div class="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
                      <Link2 class="h-5 w-5" />
                    </div>
                    <div class="min-w-0 flex-1">
                      <p class="text-sm font-semibold">{PROVIDER_LABELS[provider]}</p>
                      <Show
                        when={isLinked(provider)}
                        fallback={
                          <p class="text-xs text-muted-foreground">{t(msg`Not connected`)}</p>
                        }
                      >
                        <Badge
                          variant="secondary"
                          class="mt-1 h-5 border-none bg-primary/20 px-1.5 text-[10px] font-bold uppercase tracking-wider text-primary"
                        >
                          {t(msg`Connected`)}
                        </Badge>
                      </Show>
                    </div>

                    <Show
                      when={isLinked(provider)}
                      fallback={
                        <a
                          href={linkUrl(provider)}
                          class={buttonVariants({
                            variant: "outline",
                            size: "sm",
                          })}
                          data-testid={`link-${provider}`}
                        >
                          {t(msg`Connect`)}
                        </a>
                      }
                    >
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy() || isLastMethod(provider)}
                        title={
                          isLastMethod(provider)
                            ? t(msg`Set a password before unlinking your only sign-in method.`)
                            : undefined
                        }
                        onClick={() => setConfirmUnlink(provider)}
                        class="text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      >
                        {t(msg`Disconnect`)}
                      </Button>
                    </Show>
                  </div>
                )}
              </For>

              <Show when={!hasPassword() && totalMethods() <= 1}>
                <p class="text-xs text-muted-foreground">
                  {t(
                    msg`This is your only sign-in method. Set a password to be able to disconnect it.`,
                  )}
                </p>
              </Show>
            </Show>
          </Show>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmUnlink() !== null}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmUnlink(null);
          }
        }}
        title={t(msg`Disconnect account?`)}
        description={t(
          msg`You will no longer be able to sign in with this provider. You can reconnect it anytime.`,
        )}
        confirmLabel={t(msg`Disconnect`)}
        cancelLabel={t(msg`Cancel`)}
        onConfirm={() => {
          const provider = confirmUnlink();
          if (provider) {
            void handleUnlink(provider);
          }
        }}
      />
    </Show>
  );
}
